import { useState } from 'react';
import { 
  Phone, 
  FileText,
  Download,
  Upload,
  X,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { SubmissionLog, PayerSubmissionConfig } from '@/lib/submission/types';
import { generateFaxCoverSheet, generateFaxCoverFilename } from '@/lib/submission/faxCoverGenerator';
import { format } from 'date-fns';

interface FaxPhoneSubmissionWorkflowProps {
  caseData: AuthorizationRequest;
  submissionLog: SubmissionLog;
  config: PayerSubmissionConfig | null;
  onConfirm: (data: {
    reference_id?: string;
    confirmation_number?: string;
    proof_file_path?: string;
    proof_url?: string;
    notes?: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export function FaxPhoneSubmissionWorkflow({
  caseData,
  submissionLog,
  config,
  onConfirm,
  onCancel,
}: FaxPhoneSubmissionWorkflowProps) {
  const [mode, setMode] = useState<'fax' | 'phone'>('fax');
  const [faxNumber, setFaxNumber] = useState(config?.fax_number || caseData.payer?.fax || '');
  const [phoneNumber, setPhoneNumber] = useState(config?.phone_number || caseData.payer?.phone || '');
  const [contactName, setContactName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [callDuration, setCallDuration] = useState('');
  const [faxConfirmation, setFaxConfirmation] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleDownloadFaxCover = () => {
    const doc = generateFaxCoverSheet(caseData, {
      toName: caseData.payer?.name || 'Payer',
      toFaxNumber: faxNumber,
      fromName: 'LINK Ops',
      fromFaxNumber: '(555) 123-4567', // Would come from client settings
      fromPhone: '(555) 123-4567',
      subject: `Prior Authorization Request - ${caseData.case_number}`,
      totalPages: 1, // Would be calculated based on attached docs
      urgent: caseData.urgency === 'urgent' || caseData.urgency === 'emergent',
      message: `Please process this prior authorization request for ${caseData.procedure_description}.`,
    });

    doc.save(generateFaxCoverFilename(caseData.case_number));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        confirmation_number: mode === 'fax' ? faxConfirmation : referenceNumber,
        reference_id: referenceNumber || undefined,
        proof_url: proofUrl || undefined,
        notes: notes ? `${mode === 'phone' ? `Call duration: ${callDuration} min. ` : ''}${contactName ? `Contact: ${contactName}. ` : ''}${notes}` : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = mode === 'fax' 
    ? faxNumber && faxConfirmation
    : phoneNumber && referenceNumber;

  return (
    <Card className="border-orange-500/30 bg-orange-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Phone className="h-4 w-4 text-orange-500" />
            Fax / Phone Submission
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={mode} onValueChange={(v) => setMode(v as 'fax' | 'phone')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="fax">Fax</TabsTrigger>
            <TabsTrigger value="phone">Phone</TabsTrigger>
          </TabsList>

          <TabsContent value="fax" className="space-y-4 mt-4">
            {/* Fax Number */}
            <div>
              <Label htmlFor="faxNumber" className="text-xs">Fax Number</Label>
              <Input
                id="faxNumber"
                placeholder="(555) 123-4567"
                value={faxNumber}
                onChange={(e) => setFaxNumber(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Generate Cover Sheet */}
            <div className="bg-background rounded-lg p-3 border">
              <Label className="text-xs text-muted-foreground">Fax Cover Sheet</Label>
              <p className="text-sm mt-1 mb-2">
                Generate a cover sheet with case details to include with your fax.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadFaxCover}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download Cover Sheet
              </Button>
            </div>

            <Separator />

            {/* Fax Confirmation */}
            <div>
              <Label htmlFor="faxConfirmation" className="text-xs">
                Fax Confirmation Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="faxConfirmation"
                placeholder="Enter fax confirmation code"
                value={faxConfirmation}
                onChange={(e) => setFaxConfirmation(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="faxRef" className="text-xs">Reference Number (if provided)</Label>
              <Input
                id="faxRef"
                placeholder="Payer reference number"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="mt-1"
              />
            </div>
          </TabsContent>

          <TabsContent value="phone" className="space-y-4 mt-4">
            {/* Phone Number */}
            <div>
              <Label htmlFor="phoneNumber" className="text-xs">Phone Number</Label>
              <Input
                id="phoneNumber"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Contact Name */}
            <div>
              <Label htmlFor="contactName" className="text-xs">Contact Name (optional)</Label>
              <Input
                id="contactName"
                placeholder="Name of person you spoke with"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Call Duration */}
            <div>
              <Label htmlFor="callDuration" className="text-xs">Call Duration (minutes)</Label>
              <Input
                id="callDuration"
                type="number"
                placeholder="e.g., 15"
                value={callDuration}
                onChange={(e) => setCallDuration(e.target.value)}
                className="mt-1"
              />
            </div>

            <Separator />

            {/* Reference Number */}
            <div>
              <Label htmlFor="phoneRef" className="text-xs">
                Reference/Confirmation Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phoneRef"
                placeholder="Reference number provided over phone"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="mt-1"
              />
            </div>
          </TabsContent>
        </Tabs>

        <Separator />

        {/* Common fields */}
        <div>
          <Label htmlFor="proofUrl" className="text-xs">Proof/Screenshot URL (optional)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="proofUrl"
              placeholder="URL to screenshot or confirmation"
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              className="flex-1"
            />
            <Button variant="outline" size="icon" disabled>
              <Upload className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div>
          <Label htmlFor="notes" className="text-xs">Notes (optional)</Label>
          <Textarea
            id="notes"
            placeholder="Any additional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 min-h-[60px]"
          />
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Submission will be recorded at {format(new Date(), 'MMM d, yyyy h:mm a')}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!isValid || submitting}
            className="flex-1"
          >
            {submitting ? 'Confirming...' : 'Confirm Submission'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
